/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/media-has-caption */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaSlidersH, 
  FaImages, 
  FaPlayCircle, 
  FaCommentAlt, 
  FaCalendarAlt,
  FaStar,
  FaYoutube,
  FaCheckCircle, 
  FaTimesCircle, 
  FaTrash, 
  FaPlus,
  FaCloudUploadAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import ConfirmModal from '../common/ConfirmModal';
import './WidgetManager.css';

const WidgetManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0 to tabs.length - 1
  const [loading, setLoading] = useState(true);

  // Widget Tabs Configuration
  const [tabs, setTabs] = useState([
    { id: 1, section: 'Home Slider', icon: FaImages, status: 1, subtitle: 'Main Banner Slider', validation: 'validation-success' },
    { id: 8, section: 'Events & Exhibitions', icon: FaCalendarAlt, status: 1, subtitle: 'Featured Award & Events', validation: 'validation-success' },
    { id: 4, section: 'Left Content & Right Image', icon: FaSlidersH, status: 1, subtitle: 'About Aristo (15 Years)', validation: 'validation-success' },
    { id: 10, section: 'Why Choose Aristo', icon: FaStar, status: 1, subtitle: 'USPs & Feature Highlights', validation: 'validation-success' },
    { id: 5, section: 'Walkthrough Video', icon: FaPlayCircle, status: 1, subtitle: 'Full-Width Video Player', validation: 'validation-success' },
    { id: 11, section: 'YouTube Video Showcase', icon: FaYoutube, status: 1, subtitle: 'Craftsmanship & Process Embed', validation: 'validation-success' },
    { id: 7, section: 'Testimonial', icon: FaCommentAlt, status: 1, subtitle: 'Client Reviews', validation: 'validation-success' }
  ]);

  // Widget contents state mapped by section_id
  const [widgetContents, setWidgetContents] = useState({});
  // Categories list for dropdown selection
  const [categories, setCategories] = useState([]);
  // Products list for dropdown selection
  const [products, setProducts] = useState([]);
  const fileInputRefs = useRef({});

  // Testimonials list
  const [allTestimonials, setAllTestimonials] = useState([]);
  const [testimonialSearchQuery, setTestimonialSearchQuery] = useState('');
  const [showTestimonialDropdown, setShowTestimonialDropdown] = useState(false);
  const [showAddTestimonialModal, setShowAddTestimonialModal] = useState(false);
  const [showManageTestimonialsModal, setShowManageTestimonialsModal] = useState(false);
  const [deleteTestimonialTarget, setDeleteTestimonialTarget] = useState(null);
  const [isDeletingTestimonial, setIsDeletingTestimonial] = useState(false);

  // New testimonial modal inputs
  const [newTestimonialName, setNewTestimonialName] = useState('');
  const [newTestimonialLocation, setNewTestimonialLocation] = useState('');
  const [newTestimonialReview, setNewTestimonialReview] = useState('');
  const [newTestimonialRating, setNewTestimonialRating] = useState(5);

  useEffect(() => {
    fetchWidgetData();
    fetchCategories();
    fetchProducts();
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await axiosInstance.get('/testimonials');
      if (res && res.data && Array.isArray(res.data)) {
        setAllTestimonials(res.data);
      }
    } catch (e) {
      console.error("Error fetching testimonials:", e);
    }
  };

  const getSelectedTestimonials = () => {
    if (!currentContent || !Array.isArray(currentContent.content)) return [];
    return allTestimonials.filter(t => currentContent.content.includes(t.id));
  };

  const handleSelectTestimonial = (t) => {
    const fresh = { ...widgetContents };
    if (!Array.isArray(fresh[activeSecId].content)) {
      fresh[activeSecId].content = [];
    }
    if (!fresh[activeSecId].content.includes(t.id)) {
      fresh[activeSecId].content.push(t.id);
    }
    setWidgetContents(fresh);
    setTestimonialSearchQuery('');
    setShowTestimonialDropdown(false);
  };

  const handleRemoveSelectedTestimonial = (id) => {
    const fresh = { ...widgetContents };
    if (Array.isArray(fresh[activeSecId].content)) {
      fresh[activeSecId].content = fresh[activeSecId].content.filter(x => x !== id);
    }
    setWidgetContents(fresh);
  };

  const handleCreateTestimonial = async (e) => {
    e.preventDefault();
    if (!newTestimonialName || !newTestimonialReview) {
      toast.error("Name and Review are required!");
      return;
    }
    try {
      const res = await axiosInstance.post('/testimonials', {
        name: newTestimonialName,
        location: newTestimonialLocation,
        review: newTestimonialReview,
        rating: Number(newTestimonialRating)
      });
      if (res && res.data) {
        toast.success("Testimonial added successfully!");
        await fetchTestimonials();
        
        if (activeSecId === 7) {
          const fresh = { ...widgetContents };
          if (!Array.isArray(fresh[activeSecId].content)) {
            fresh[activeSecId].content = [];
          }
          if (!fresh[activeSecId].content.includes(res.data.id)) {
            fresh[activeSecId].content.push(res.data.id);
          }
          setWidgetContents(fresh);
        }
        
        setNewTestimonialName('');
        setNewTestimonialLocation('');
        setNewTestimonialReview('');
        setNewTestimonialRating(5);
        setShowAddTestimonialModal(false);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add testimonial");
    }
  };

  const handleDeleteTestimonial = (testimonial) => {
    setDeleteTestimonialTarget(testimonial);
  };

  const handleConfirmDeleteTestimonial = async () => {
    if (!deleteTestimonialTarget) return;
    try {
      setIsDeletingTestimonial(true);
      const res = await axiosInstance.delete(`/testimonials/${deleteTestimonialTarget.id}`);
      if (res && res.data && res.data.success) {
        toast.success("Testimonial deleted successfully!");
        
        const fresh = { ...widgetContents };
        if (fresh[7] && Array.isArray(fresh[7].content)) {
          fresh[7].content = fresh[7].content.filter(x => x !== deleteTestimonialTarget.id);
          setWidgetContents(fresh);
        }
        
        fetchTestimonials();
        setDeleteTestimonialTarget(null);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete testimonial");
    } finally {
      setIsDeletingTestimonial(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axiosInstance.get('/products');
      if (res && res.data && Array.isArray(res.data)) {
        setProducts(res.data);
      }
    } catch (e) {
      console.error("Error fetching products:", e);
    }
  };

  const renderPageRedirectOptions = () => {
    const mainCats = categories.filter(c => c.type === 'main');
    const subCats = categories.filter(c => c.type === 'sub');
    return (
      <>
        <option value="">Select Page / Category</option>
        <optgroup label="Pages" style={{ color: '#c4a882', background: '#0b0f19' }}>
          <option value="/">Home</option>
          <option value="/about-us">About Us</option>
          <option value="/blogs">Blogs</option>
          <option value="/events">Events</option>
        </optgroup>
        <optgroup label="Category" style={{ color: '#c4a882', background: '#0b0f19' }}>
          {mainCats.map(cat => (
            <option key={cat.id} value={`category-${cat.id}`}>{cat.title}</option>
          ))}
        </optgroup>
        <optgroup label="Sub Category" style={{ color: '#c4a882', background: '#0b0f19' }}>
          {subCats.map(cat => (
            <option key={cat.id} value={`subcategory-${cat.id}`}>{cat.title}</option>
          ))}
        </optgroup>
        <optgroup label="Products" style={{ color: '#c4a882', background: '#0b0f19' }}>
          {products.map(prod => (
            <option key={prod.id} value={`product-${prod.id}`}>{prod.product}</option>
          ))}
        </optgroup>
      </>
    );
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(4px)'
  };

  const modalContentStyle = {
    background: '#0b1329',
    border: '1px solid #1e293b',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '550px',
    padding: '2rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
    maxHeight: '85vh',
    overflowY: 'auto'
  };

  const modalHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #1e293b',
    paddingBottom: '1rem',
    marginBottom: '1.5rem'
  };

  const modalTitleStyle = {
    fontSize: '1.25rem',
    color: '#ffffff',
    fontWeight: 'bold',
    margin: 0
  };

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get('/categories');
      if (res && res.data) {
        // Handle various category response structures
        const list = Array.isArray(res.data) ? res.data : (res.data.categories || []);
        setCategories(list);
      }
    } catch (e) {
      console.error("Error fetching categories:", e);
    }
  };

  const fetchWidgetData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/pages/widgets');
      if (res && res.data && res.data.success) {
        const { sections, contents } = res.data;

        // 1. Sync section status toggles
        if (sections && Array.isArray(sections)) {
          const updatedTabs = tabs.map(t => {
            const match = sections.find(s => Number(s.id) === Number(t.id));
            if (match) {
              return {
                ...t,
                status: match.status === 1 ? 1 : 0,
                validation: match.status === 1 ? 'validation-success' : 'validation-error'
              };
            }
            return t;
          });
          setTabs(updatedTabs);
        }

        // 2. Parse contents by section_id
        const contentMap = {};
        if (contents && Array.isArray(contents)) {
          contents.forEach(item => {
            let parsed = [];
            try {
              parsed = typeof item.content === 'string' ? JSON.parse(item.content) : (item.content || []);
            } catch (e) {
              console.error(`Error parsing widget content for section ${item.section_id}:`, e);
            }
            contentMap[item.section_id] = {
              id: item.id,
              section_id: item.section_id,
              optional_type: item.optional_type || 0,
              optional_content: item.optional_content || '',
              status: item.status === 1 ? 1 : 0,
              content: parsed
            };
          });
        }
        setWidgetContents(contentMap);
      }
    } catch (err) {
      console.error("Error loading widgets data:", err);
      toast.error("Failed to load Widget Manager configurations");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('blob:') || image.startsWith('http')) return image;
    const backendHost = (process.env.REACT_APP_BACKEND || (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) || 'http://localhost:8000').replace(/\/$/, '');
    return `${backendHost}/uploads/${image}`;
  };

  // Toggle active tab sidebar status (Persists immediately to backend)
  const handleToggleSectionStatus = async (idx, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const updated = [...tabs];
    const newStatus = updated[idx].status === 1 ? 0 : 1;
    updated[idx] = {
      ...updated[idx],
      status: newStatus,
      validation: newStatus === 1 ? 'validation-success' : 'validation-error'
    };
    setTabs(updated);

    // Sync content status
    const secId = updated[idx].id;
    let freshWidgetContents = { ...widgetContents };
    if (freshWidgetContents[secId]) {
      freshWidgetContents = {
        ...freshWidgetContents,
        [secId]: {
          ...freshWidgetContents[secId],
          status: newStatus
        }
      };
      setWidgetContents(freshWidgetContents);
    }

    try {
      const formattedSections = updated.map(t => ({
        id: t.id,
        status: t.status
      }));
      const formattedContents = Object.keys(freshWidgetContents).map(key => {
        const item = freshWidgetContents[key];
        const cleanedContent = Array.isArray(item.content) 
          ? item.content.map(s => {
              const copy = { ...s };
              delete copy._file;
              return copy;
            })
          : item.content;
        return {
          section_id: item.section_id,
          optional_type: item.optional_type || 0,
          optional_content: item.optional_content || '',
          status: item.section_id === secId ? newStatus : (item.status ?? 1),
          content: cleanedContent
        };
      });

      await axiosInstance.patch('/pages/widgets', {
        sections: formattedSections,
        contents: formattedContents
      });
      toast.success(`${updated[idx].section} status is now ${newStatus === 1 ? 'Active' : 'Inactive'}!`);
    } catch (err) {
      console.error("Auto-save status toggle failed:", err);
    }
  };

  // Upload handler for slide images
  const handleSlideImageChange = async (e, secId, slideIdx) => {
    const file = e.target.files[0];
    if (!file) return;

    const tempUrl = URL.createObjectURL(file);
    const updated = { ...widgetContents };
    if (!updated[secId] || !Array.isArray(updated[secId].content)) return;

    updated[secId].content[slideIdx].image = tempUrl;
    updated[secId].content[slideIdx]._file = file;
    setWidgetContents({ ...updated });

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axiosInstance.post('/products/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data && res.data.filename) {
        const finalUrl = res.data.filename;
        const fresh = { ...widgetContents };
        fresh[secId].content[slideIdx].image = finalUrl;
        delete fresh[secId].content[slideIdx]._file;
        setWidgetContents({ ...fresh });
        toast.success(`Image uploaded successfully!`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Image upload failed');
    }
  };

  // Slides / Items CRUD operations helper
  const handleAddSlideItem = (secId, defaultProps = {}) => {
    const fresh = { ...widgetContents };
    if (!fresh[secId]) {
      fresh[secId] = {
        section_id: secId,
        optional_type: 0,
        optional_content: '',
        status: 1,
        content: []
      };
    }
    const newSlide = {
      id: String(Date.now()),
      heading: '',
      description: '',
      image: '',
      alt: '',
      status: 1,
      sort: String(fresh[secId].content.length + 1),
      assign_btn: '0',
      urlType1: '',
      buttonText1: '',
      buttonUrl1: '',
      urlType2: '',
      buttonText2: '',
      buttonUrl2: '',
      ...defaultProps
    };
    fresh[secId].content.push(newSlide);
    setWidgetContents(fresh);
  };

  const handleRemoveSlideItem = (secId, slideIdx) => {
    const fresh = { ...widgetContents };
    if (fresh[secId] && Array.isArray(fresh[secId].content)) {
      if (fresh[secId].content.length === 1) {
        toast.warning('At least one item configuration is required.');
        return;
      }
      fresh[secId].content.splice(slideIdx, 1);
      setWidgetContents(fresh);
    }
  };

  // Save changes to database
  const handleSaveAll = async () => {
    try {
      // Build payload matching DB tables schema
      const formattedSections = tabs.map(t => ({
        id: t.id,
        status: t.status
      }));

      const formattedContents = Object.keys(widgetContents).map(key => {
        const item = widgetContents[key];
        // Clean temporary blob file keys
        const cleanedContent = Array.isArray(item.content) 
          ? item.content.map(s => {
              const copy = { ...s };
              delete copy._file;
              return copy;
            })
          : item.content;

        return {
          section_id: item.section_id,
          optional_type: item.optional_type,
          optional_content: item.optional_content,
          status: item.status,
          content: cleanedContent
        };
      });

      const res = await axiosInstance.patch('/pages/widgets', {
        sections: formattedSections,
        contents: formattedContents
      });

      if (res.data && res.data.success) {
        toast.success('Widget Manager saved successfully!');
        fetchWidgetData(); // Refresh UI state
      } else {
        toast.error(res.data.message || 'Failed to update widgets');
      }
    } catch (e) {
      console.error(e);
      toast.error('Server error while saving Widget configurations');
    }
  };

  if (loading) {
    return (
      <div className="edit-widget-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <h3 style={{ color: '#c4a882' }}>Loading Widget Manager Configuration...</h3>
      </div>
    );
  }

  // Active section ID mapped by active Tab index
  const currentTab = tabs[activeTab];
  const activeSecId = currentTab.id;
  const currentContent = widgetContents[activeSecId] || {
    section_id: activeSecId,
    optional_type: 0,
    optional_content: '',
    status: currentTab.status,
    content: []
  };

  return (
    <div className="premium-page-container animate-fade-up">
      <div className="premium-page-header">
        <h1 className="page-title">Widget Manager</h1>
      </div>

      <div className="edit-widget-grid">
        {/* Sticky Left Sidebar Navigation */}
        <div className="edit-widget-sidebar">
          <ul className="edit-widget-nav-list">
            {tabs.map((tab, idx) => {
              const IconComp = tab.icon;
              return (
                <li 
                  key={tab.id}
                  className={`edit-widget-nav-item ${activeTab === idx ? 'active' : ''}`}
                  onClick={() => setActiveTab(idx)}
                >
                  <div className="edit-widget-nav-left">
                    <span className="edit-widget-nav-icon"><IconComp /></span>
                    <div className="edit-widget-nav-text">
                      <span className="nav-item-title">{tab.section}</span>
                      <span className="nav-item-subtitle">{tab.subtitle}</span>
                    </div>
                  </div>

                  <div className="edit-widget-nav-right">
                    <span className={`status-indicator ${tab.validation}`}>
                      {tab.validation === 'validation-success' && <FaCheckCircle />}
                      {tab.validation === 'validation-error' && <FaTimesCircle />}
                    </span>

                    <label className="switch-toggle" onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={tab.status === 1}
                        onChange={(e) => handleToggleSectionStatus(idx, e)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Scrollable Right Panel content */}
        <div className="edit-widget-content-panel">
          
          {/* If disabled, show disabled overlay */}
          {currentTab.status === 0 ? (
            <div className="oops-disabled-overlay animate-fade-up">
              <div className="oops-card">
                <div className="oops-icon">✖</div>
                <h3 className="oops-title">Oops!</h3>
                <p className="oops-message">Please enable or activate {currentTab.section} to modify the content.</p>
                <button 
                  type="button" 
                  className="btn-enable-oops"
                  onClick={() => {
                    const updated = [...tabs];
                    updated[activeTab].status = 1;
                    updated[activeTab].validation = 'validation-success';
                    setTabs(updated);
                    
                    if (widgetContents[activeSecId]) {
                      setWidgetContents(prev => ({
                        ...prev,
                        [activeSecId]: { ...prev[activeSecId], status: 1 }
                      }));
                    }
                  }}
                >
                  Enable / Activate
                </button>
              </div>
            </div>
          ) : (
            <div className="tab-pane animate-fade-up">
              <h2 className="edit-widget-panel-title">{currentTab.section}</h2>

              {/* RENDER MODE: Events & Exhibitions (section_id = 8) */}
              {activeSecId === 8 && (() => {
                const eventItem = (Array.isArray(currentContent.content) && currentContent.content.length > 0)
                  ? currentContent.content[0]
                  : (currentContent.content || {});

                const updateEventField = (field, val) => {
                  const fresh = { ...widgetContents };
                  if (!fresh[8]) fresh[8] = { section_id: 8, content: [{}] };
                  if (!Array.isArray(fresh[8].content)) fresh[8].content = [{}];
                  if (fresh[8].content.length === 0) fresh[8].content = [{}];
                  fresh[8].content[0][field] = val;
                  setWidgetContents(fresh);
                };

                return (
                  <div className="widget-card-block animate-fade-up">
                    {/* 1. Header Information (Tag & Main Heading) */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginBottom: '1.25rem' }}>
                      <div className="form-group-custom" style={{ margin: 0 }}>
                        <label className="label-custom">Badge Tag <span className="req">*</span></label>
                        <input 
                          type="text" 
                          className="input-custom"
                          placeholder="e.g. EVENTS & EXHIBITIONS"
                          value={eventItem.tag || ''}
                          onChange={(e) => updateEventField('tag', e.target.value)}
                        />
                      </div>

                      <div className="form-group-custom" style={{ margin: 0 }}>
                        <label className="label-custom">Main Heading <span className="req">*</span></label>
                        <input 
                          type="text" 
                          className="input-custom"
                          placeholder="e.g. Where Innovation Meets You, In Person"
                          value={eventItem.heading || ''}
                          onChange={(e) => updateEventField('heading', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* 2. Descriptions */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                      <div className="form-group-custom" style={{ margin: 0 }}>
                        <label className="label-custom">Description Paragraph 1 <span className="req">*</span></label>
                        <textarea 
                          className="textarea-custom"
                          rows={5}
                          placeholder="Write first paragraph..."
                          value={eventItem.desc1 || ''}
                          onChange={(e) => updateEventField('desc1', e.target.value)}
                        />
                      </div>

                      <div className="form-group-custom" style={{ margin: 0 }}>
                        <label className="label-custom">Description Paragraph 2 <span className="req">*</span></label>
                        <textarea 
                          className="textarea-custom"
                          rows={5}
                          placeholder="Write second paragraph..."
                          value={eventItem.desc2 || ''}
                          onChange={(e) => updateEventField('desc2', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* 3. Stat Highlights (4 Counters) */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <label className="label-custom" style={{ marginBottom: '0.75rem', display: 'block' }}>Key Highlights & Stat Counters</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                        {/* Stat 1 */}
                        <div style={{ background: '#070c18', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.75rem', color: '#c4a882', fontWeight: 'bold' }}>Counter 1</span>
                          <input 
                            type="text" 
                            className="input-custom" 
                            style={{ margin: '0.35rem 0' }}
                            placeholder="Value (e.g. 50+)"
                            value={eventItem.stat1_val || ''}
                            onChange={(e) => updateEventField('stat1_val', e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Label (e.g. Exhibitions Attended)"
                            value={eventItem.stat1_lbl || ''}
                            onChange={(e) => updateEventField('stat1_lbl', e.target.value)}
                          />
                        </div>

                        {/* Stat 2 */}
                        <div style={{ background: '#070c18', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.75rem', color: '#c4a882', fontWeight: 'bold' }}>Counter 2</span>
                          <input 
                            type="text" 
                            className="input-custom" 
                            style={{ margin: '0.35rem 0' }}
                            placeholder="Value (e.g. 16+)"
                            value={eventItem.stat2_val || ''}
                            onChange={(e) => updateEventField('stat2_val', e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Label (e.g. Showroom Launches)"
                            value={eventItem.stat2_lbl || ''}
                            onChange={(e) => updateEventField('stat2_lbl', e.target.value)}
                          />
                        </div>

                        {/* Stat 3 */}
                        <div style={{ background: '#070c18', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.75rem', color: '#c4a882', fontWeight: 'bold' }}>Counter 3</span>
                          <input 
                            type="text" 
                            className="input-custom" 
                            style={{ margin: '0.35rem 0' }}
                            placeholder="Value (e.g. 12+)"
                            value={eventItem.stat3_val || ''}
                            onChange={(e) => updateEventField('stat3_val', e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Label (e.g. Industry Awards)"
                            value={eventItem.stat3_lbl || ''}
                            onChange={(e) => updateEventField('stat3_lbl', e.target.value)}
                          />
                        </div>

                        {/* Stat 4 */}
                        <div style={{ background: '#070c18', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.75rem', color: '#c4a882', fontWeight: 'bold' }}>Counter 4</span>
                          <input 
                            type="text" 
                            className="input-custom" 
                            style={{ margin: '0.35rem 0' }}
                            placeholder="Value (e.g. 5000+)"
                            value={eventItem.stat4_val || ''}
                            onChange={(e) => updateEventField('stat4_val', e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Label (e.g. Visitors Engaged)"
                            value={eventItem.stat4_lbl || ''}
                            onChange={(e) => updateEventField('stat4_lbl', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 4. Button & Image Uploader Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: '1.5rem', alignItems: 'start' }}>
                      {/* Left: Button Text & URL + Alt */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Button Text <span className="req">*</span></label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. View All Events"
                            value={eventItem.btn_text || ''}
                            onChange={(e) => updateEventField('btn_text', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Button Target URL <span className="req">*</span></label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. /events"
                            value={eventItem.btn_url || ''}
                            onChange={(e) => updateEventField('btn_url', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Featured Image Alt Text</label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. Times Business Awards 2024"
                            value={eventItem.alt || ''}
                            onChange={(e) => updateEventField('alt', e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Right: Featured Award / Event Image */}
                      <div>
                        <label className="label-custom">Featured Award / Event Image</label>
                        <div 
                          className="widget-uploader-wrapper"
                          style={{ height: '190px', background: '#0b101d' }}
                          onClick={() => fileInputRefs.current['event_image']?.click()}
                        >
                          {eventItem.image ? (
                            <>
                              <img src={getImageUrl(eventItem.image)} alt={eventItem.alt || 'Award'} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '6px' }} />
                              <div className="image-delete-overlay" onClick={(e) => { e.stopPropagation(); updateEventField('image', ''); }}>
                                <FaTrash size={20} />
                              </div>
                            </>
                          ) : (
                            <div className="uploader-placeholder">
                              <FaCloudUploadAlt size={36} />
                              <span>Click to upload Event / Award Image</span>
                            </div>
                          )}
                        </div>
                        <input 
                          type="file" 
                          accept="image/*"
                          style={{ display: 'none' }}
                          ref={el => fileInputRefs.current['event_image'] = el}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('image', file);
                              try {
                                const res = await axiosInstance.post('/products/upload', formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                if (res.data && res.data.filename) {
                                  updateEventField('image', res.data.filename);
                                  toast.success('Event image uploaded successfully!');
                                }
                              } catch (err) {
                                console.error('Image upload failed:', err);
                                toast.error('Failed to upload image');
                              }
                            }
                          }}
                        />
                        <span className="helper-text">Recommended: 800 × 600 px or Certificate layout.</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* RENDER MODE A: Interior Solutions (Single Title + status toggles only) */}
              {activeSecId === 9 && (
                <div className="widget-card-block">
                  <div className="form-group-custom">
                    <label className="label-custom">Section Title <span className="req">*</span></label>
                    <input 
                      type="text" 
                      className="input-custom"
                      value={currentContent.optional_content || ''}
                      onChange={(e) => {
                        setWidgetContents(prev => ({
                          ...prev,
                          [activeSecId]: {
                            ...prev[activeSecId],
                            optional_content: e.target.value
                          }
                        }));
                      }}
                      placeholder="Enter Section Title..."
                    />
                  </div>
                  <span className="helper-text">This updates the title text displayed on the website section block. List items are managed dynamically in their dedicated managers.</span>
                </div>
              )}

              {/* RENDER MODE F: Testimonial Customised Sort Editor */}
              {activeSecId === 7 && (
                <div className="widget-card-block">
                  <div className="form-group-custom">
                    <label className="label-custom">Section Title <span className="req">*</span></label>
                    <input 
                      type="text" 
                      className="input-custom"
                      value={currentContent.optional_content || ''}
                      onChange={(e) => {
                        setWidgetContents(prev => ({
                          ...prev,
                          [activeSecId]: {
                            ...prev[activeSecId],
                            optional_content: e.target.value
                          }
                        }));
                      }}
                      placeholder="Enter Section Title..."
                    />
                  </div>

                  <div className="form-group-custom" style={{ marginTop: '1.5rem' }}>
                    <label className="label-custom">Sort Testimonials by</label>
                    <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#ffffff' }}>
                        <input 
                          type="radio" 
                          name="sortTestimonials" 
                          value="0" 
                          checked={String(currentContent.optional_type) === '0' || !currentContent.optional_type}
                          onChange={() => {
                            setWidgetContents(prev => ({
                              ...prev,
                              [activeSecId]: { ...prev[activeSecId], optional_type: '0' }
                            }));
                          }}
                          style={{ accentColor: '#c4a882', width: '18px', height: '18px' }}
                        />
                        Latest
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#ffffff' }}>
                        <input 
                          type="radio" 
                          name="sortTestimonials" 
                          value="1" 
                          checked={String(currentContent.optional_type) === '1'}
                          onChange={() => {
                            setWidgetContents(prev => ({
                              ...prev,
                              [activeSecId]: { ...prev[activeSecId], optional_type: '1' }
                            }));
                          }}
                          style={{ accentColor: '#c4a882', width: '18px', height: '18px' }}
                        />
                        Customised
                      </label>
                    </div>
                  </div>

                  {String(currentContent.optional_type) === '1' && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <div className="form-group-custom">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <label className="label-custom">Select Testimonial <span className="req">*</span></label>
                          <div style={{ display: 'flex', gap: '1.5rem' }}>
                            <span 
                              onClick={() => setShowAddTestimonialModal(true)}
                              style={{ color: '#c4a882', textDecoration: 'none', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              + Add to list
                            </span>
                            <span 
                              onClick={() => setShowManageTestimonialsModal(true)}
                              style={{ color: '#c4a882', textDecoration: 'none', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              + Manage Testimonials
                            </span>
                          </div>
                        </div>
                        
                        <div style={{ position: 'relative' }}>
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Search testimonials..." 
                            value={testimonialSearchQuery}
                            onChange={(e) => {
                              setTestimonialSearchQuery(e.target.value);
                              setShowTestimonialDropdown(true);
                            }}
                            onFocus={() => setShowTestimonialDropdown(true)}
                          />
                          
                          {showTestimonialDropdown && testimonialSearchQuery && (
                            <div className="search-suggestions-dropdown" style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              background: '#0f172a',
                              border: '1px solid #1e293b',
                              borderRadius: '6px',
                              zIndex: 1000,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              marginTop: '4px'
                            }}>
                              {allTestimonials
                                .filter(t => t.name.toLowerCase().includes(testimonialSearchQuery.toLowerCase()) || 
                                             t.review.toLowerCase().includes(testimonialSearchQuery.toLowerCase()))
                                .map(t => (
                                  <div 
                                    key={t.id} 
                                    onClick={() => handleSelectTestimonial(t)}
                                    style={{
                                      padding: '0.75rem 1rem',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #1e293b',
                                      color: '#ffffff',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '0.25rem'
                                    }}
                                    className="suggestion-item"
                                  >
                                    <strong style={{ color: '#c4a882' }}>{t.name} ({t.location || 'No Location'})</strong>
                                    <span style={{ fontSize: '0.85rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {t.review}
                                    </span>
                                  </div>
                                ))
                              }
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="selected-testimonials-container" style={{
                        border: '1px solid #1e293b',
                        borderRadius: '8px',
                        background: '#070a13',
                        padding: '1.5rem',
                        marginTop: '1.5rem',
                        minHeight: '80px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        alignItems: getSelectedTestimonials().length === 0 ? 'center' : 'stretch',
                        justifyContent: getSelectedTestimonials().length === 0 ? 'center' : 'flex-start'
                      }}>
                        {getSelectedTestimonials().length === 0 ? (
                          <div style={{ color: '#64748b', fontSize: '0.95rem' }}>No testimonials selected</div>
                        ) : (
                          getSelectedTestimonials().map(t => (
                            <div 
                              key={t.id} 
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: '#0f172a',
                                border: '1px solid #1e293b',
                                borderRadius: '6px',
                                padding: '1rem'
                              }}
                            >
                              <div>
                                <strong style={{ color: '#c4a882' }}>{t.name} ({t.location || 'No Location'})</strong>
                                <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginTop: '0.25rem' }}>&ldquo;{t.review}&rdquo;</div>
                              </div>
                              <button 
                                type="button" 
                                onClick={() => handleRemoveSelectedTestimonial(t.id)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <FaTrash size={16} />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* RENDER MODE B: Tile View (Tile list + title header) */}
              {activeSecId === 6 && (
                <>
                  <div className="widget-card-block" style={{ marginBottom: '1.5rem' }}>
                    <div className="form-group-custom">
                      <label className="label-custom">Highlight Section Category Title <span className="req">*</span></label>
                      <input 
                        type="text" 
                        className="input-custom"
                        value={currentContent.optional_content || ''}
                        onChange={(e) => {
                          setWidgetContents(prev => ({
                            ...prev,
                            [activeSecId]: {
                              ...prev[activeSecId],
                              optional_content: e.target.value
                            }
                          }));
                        }}
                        placeholder="e.g. Wardrobe Designs 2026"
                      />
                    </div>
                  </div>

                  {Array.isArray(currentContent.content) && currentContent.content.map((slide, sIdx) => (
                    <div key={slide.id || sIdx} className="widget-card-block" style={{ marginBottom: '1.5rem' }}>
                      <div className="slide-header-bar">
                        <h3 className="slide-number-title">Tile {sIdx + 1}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <label className="switch-toggle" style={{ transform: 'scale(0.85)', marginBottom: 0 }}>
                            <input 
                              type="checkbox" 
                              checked={slide.status === 1 || slide.status === '1'}
                              onChange={(e) => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].status = e.target.checked ? 1 : 0;
                                setWidgetContents(fresh);
                              }}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <button 
                            type="button" 
                            className="btn-trash-icon"
                            onClick={() => handleRemoveSlideItem(activeSecId, sIdx)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>

                      {/* Tile Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1.5rem' }}>
                        <div>
                          <label className="label-custom">Tile Image <span className="req">*</span></label>
                          <div 
                            className="widget-uploader-wrapper"
                            style={{ background: slide.image ? '#ffffff' : 'transparent' }}
                            onClick={() => fileInputRefs.current[`tile_img_${sIdx}`]?.click()}
                          >
                            {slide.image ? (
                              <>
                                <img src={getImageUrl(slide.image)} alt={slide.alt} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }} />
                                <div className="image-delete-overlay" onClick={(e) => { e.stopPropagation(); const fresh = { ...widgetContents }; fresh[activeSecId].content[sIdx].image = ''; setWidgetContents(fresh); }}>
                                  <FaTrash size={20} />
                                </div>
                              </>
                            ) : (
                              <div className="uploader-placeholder">
                                <FaCloudUploadAlt size={36} />
                                <span>Upload Tile Image</span>
                              </div>
                            )}
                          </div>
                          <input 
                            type="file" 
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={el => fileInputRefs.current[`tile_img_${sIdx}`] = el}
                            onChange={(e) => handleSlideImageChange(e, activeSecId, sIdx)}
                          />
                          <span className="helper-text" style={{ display: 'block', marginBottom: '1rem' }}>Image dimensions 800 × 500 px.</span>

                          <div className="form-group-custom">
                            <label className="label-custom">Alt Text <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Alt Text"
                              value={slide.alt || ''}
                              onChange={(e) => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].alt = e.target.value;
                                setWidgetContents(fresh);
                              }}
                            />
                          </div>
                        </div>

                        <div>
                          <div className="form-group-custom">
                            <label className="label-custom">Tile Heading <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Tile Heading"
                              value={slide.heading || ''}
                              onChange={(e) => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].heading = e.target.value;
                                setWidgetContents(fresh);
                              }}
                            />
                          </div>

                          <div className="form-group-custom">
                            <label className="label-custom">Link Target Category <span className="req">*</span></label>
                            <select 
                              className="input-custom"
                              value={slide.type || ''}
                              onChange={(e) => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].type = e.target.value;
                                setWidgetContents(fresh);
                              }}
                            >
                              {renderPageRedirectOptions()}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                    <button 
                      type="button" 
                      className="btn-add-dashed"
                      onClick={() => handleAddSlideItem(activeSecId, { type: '', heading: '', image: '', alt: '' })}
                    >
                      <FaPlus /> Add Tile Item
                    </button>
                  </div>
                </>
              )}

              {/* RENDER MODE C: Walkthrough Video Player (section_id = 5) */}
              {activeSecId === 5 && (() => {
                const item = (Array.isArray(currentContent.content) && currentContent.content.length > 0)
                  ? currentContent.content[0]
                  : (currentContent.content || {});

                const updateVideoField = (field, val) => {
                  const fresh = { ...widgetContents };
                  if (!fresh[5]) fresh[5] = { section_id: 5, content: [{}] };
                  if (!Array.isArray(fresh[5].content)) fresh[5].content = [{}];
                  if (fresh[5].content.length === 0) fresh[5].content = [{}];
                  fresh[5].content[0][field] = val;
                  setWidgetContents(fresh);
                };

                const rawVideo = item.video || 'kichenevideo.mp4';
                const videoPreviewSrc = rawVideo.startsWith('blob:') 
                  ? rawVideo 
                  : (rawVideo.startsWith('http') || rawVideo.startsWith('/')) 
                    ? rawVideo 
                    : getImageUrl(rawVideo);

                return (
                  <div className="widget-card-block animate-fade-up">
                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem', alignItems: 'start' }}>
                      {/* Left: Video Player Preview & Upload Box */}
                      <div>
                        <label className="label-custom">Video File (.mp4 / .webm) <span className="req">*</span></label>
                        <div 
                          style={{ 
                            position: 'relative',
                            width: '100%', 
                            aspectRatio: '16/9', 
                            background: '#000000', 
                            borderRadius: '12px', 
                            overflow: 'hidden',
                            border: '1px solid #1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '1rem'
                          }}
                        >
                          {item.video ? (
                            <>
                              <video 
                                src={videoPreviewSrc} 
                                controls 
                                playsInline 
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                              <div 
                                className="image-delete-overlay" 
                                style={{ position: 'absolute', top: '10px', right: '10px', width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}
                                onClick={(e) => { e.stopPropagation(); updateVideoField('video', ''); }}
                                title="Remove Video"
                              >
                                <FaTrash size={16} color="#ffffff" />
                              </div>
                            </>
                          ) : (
                            <div 
                              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', color: '#64748b', cursor: 'pointer', padding: '2rem' }}
                              onClick={() => fileInputRefs.current['walkthrough_video_file']?.click()}
                            >
                              <FaPlayCircle size={48} style={{ color: '#c4a882' }} />
                              <span style={{ color: '#c4a882', fontWeight: 'bold' }}>Click to Upload MP4 Video File</span>
                              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Supports MP4, WebM, MOV files</span>
                            </div>
                          )}
                        </div>

                        {/* File upload button */}
                        <div style={{ display: 'flex', gap: '1rem' }}>
                          <button 
                            type="button" 
                            className="btn-add-dashed"
                            style={{ flex: 1, margin: 0, padding: '0.65rem' }}
                            onClick={() => fileInputRefs.current['walkthrough_video_file']?.click()}
                          >
                            <FaCloudUploadAlt size={18} /> {item.video ? 'Replace Video File' : 'Upload Video File'}
                          </button>
                        </div>
                        <input 
                          type="file" 
                          accept="video/mp4,video/webm,video/*"
                          style={{ display: 'none' }}
                          ref={el => fileInputRefs.current['walkthrough_video_file'] = el}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const tempUrl = URL.createObjectURL(file);
                              updateVideoField('video', tempUrl);
                              const formData = new FormData();
                              formData.append('video', file);
                              try {
                                const res = await axiosInstance.post('/products/upload-video', formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                if (res.data && res.data.filename) {
                                  updateVideoField('video', res.data.filename);
                                  toast.success('Video file uploaded successfully!');
                                }
                              } catch (err) {
                                console.error('Video upload failed:', err);
                                toast.error('Failed to upload video file');
                              }
                            }
                          }}
                        />
                      </div>

                      {/* Right: Poster Image & Playback Settings */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Video Title / Label</label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. Kitchen Walkthrough Video"
                            value={item.heading || ''}
                            onChange={(e) => updateVideoField('heading', e.target.value)}
                          />
                        </div>

                        {/* Poster / Cover Image */}
                        <div>
                          <label className="label-custom">Video Cover / Poster Image <span className="opt">(Optional)</span></label>
                          <div 
                            className="widget-uploader-wrapper"
                            style={{ height: '110px', background: '#0b101d' }}
                            onClick={() => fileInputRefs.current['video_poster_img']?.click()}
                          >
                            {item.poster || item.image ? (
                              <>
                                <img src={getImageUrl(item.poster || item.image)} alt="Poster" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div className="image-delete-overlay" onClick={(e) => { e.stopPropagation(); updateVideoField('poster', ''); updateVideoField('image', ''); }}>
                                  <FaTrash size={16} />
                                </div>
                              </>
                            ) : (
                              <div className="uploader-placeholder">
                                <FaCloudUploadAlt size={24} />
                                <span>Upload Poster Thumbnail</span>
                              </div>
                            )}
                          </div>
                          <input 
                            type="file" 
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={el => fileInputRefs.current['video_poster_img'] = el}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const formData = new FormData();
                                formData.append('image', file);
                                try {
                                  const res = await axiosInstance.post('/products/upload', formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  if (res.data && res.data.filename) {
                                    updateVideoField('poster', res.data.filename);
                                    updateVideoField('image', res.data.filename);
                                    toast.success('Poster image uploaded successfully!');
                                  }
                                } catch (err) {
                                  console.error('Image upload failed:', err);
                                  toast.error('Failed to upload image');
                                }
                              }
                            }}
                          />
                        </div>

                        {/* Playback Toggles */}
                        <div style={{ background: '#070c18', padding: '1rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.85rem', color: '#c4a882', fontWeight: 'bold', display: 'block', marginBottom: '0.75rem' }}>Playback Options</span>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: '#ffffff', fontSize: '0.85rem' }}>
                              <span>Auto-play Video</span>
                              <input 
                                type="checkbox" 
                                style={{ accentColor: '#c4a882', width: '16px', height: '16px' }}
                                checked={item.autoplay !== 0 && item.autoplay !== '0'}
                                onChange={(e) => updateVideoField('autoplay', e.target.checked ? 1 : 0)}
                              />
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: '#ffffff', fontSize: '0.85rem' }}>
                              <span>Loop Continuously</span>
                              <input 
                                type="checkbox" 
                                style={{ accentColor: '#c4a882', width: '16px', height: '16px' }}
                                checked={item.loop !== 0 && item.loop !== '0'}
                                onChange={(e) => updateVideoField('loop', e.target.checked ? 1 : 0)}
                              />
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: '#ffffff', fontSize: '0.85rem' }}>
                              <span>Default Muted (Recommended for Auto-play)</span>
                              <input 
                                type="checkbox" 
                                style={{ accentColor: '#c4a882', width: '16px', height: '16px' }}
                                checked={item.muted !== 0 && item.muted !== '0'}
                                onChange={(e) => updateVideoField('muted', e.target.checked ? 1 : 0)}
                              />
                            </label>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* RENDER MODE: YouTube Video Showcase (section_id = 11) */}
              {activeSecId === 11 && (() => {
                const item = (Array.isArray(currentContent.content) && currentContent.content.length > 0)
                  ? currentContent.content[0]
                  : (currentContent.content || {});

                const updateYoutubeField = (field, val) => {
                  const fresh = { ...widgetContents };
                  if (!fresh[11]) fresh[11] = { section_id: 11, content: [{}] };
                  if (!Array.isArray(fresh[11].content)) fresh[11].content = [{}];
                  if (fresh[11].content.length === 0) fresh[11].content = [{}];
                  fresh[11].content[0][field] = val;
                  setWidgetContents(fresh);
                };

                const currentFeatures = Array.isArray(item.features) ? item.features : [
                  'Advanced German Machinery',
                  'Skilled Designers & Craftsmen',
                  'Quality Checks at Every Stage',
                  'Precision Engineering & Seamless Installation',
                  'Lifetime Service & Dedicated After-Sales Support'
                ];

                const updateFeatureItem = (fIdx, val) => {
                  const updatedFeatures = [...currentFeatures];
                  updatedFeatures[fIdx] = val;
                  updateYoutubeField('features', updatedFeatures);
                };

                const addFeatureItem = () => {
                  updateYoutubeField('features', [...currentFeatures, '']);
                };

                const removeFeatureItem = (fIdx) => {
                  const updatedFeatures = currentFeatures.filter((_, idx) => idx !== fIdx);
                  updateYoutubeField('features', updatedFeatures);
                };

                const getEmbedUrl = (url) => {
                  if (!url) return '';
                  if (url.length === 11 && !url.includes('/') && !url.includes('.')) {
                    return `https://www.youtube.com/embed/${url}`;
                  }
                  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                  const match = url.match(regExp);
                  if (match && match[2].length === 11) {
                    return `https://www.youtube.com/embed/${match[2]}`;
                  }
                  return url;
                };

                return (
                  <div className="widget-card-block animate-fade-up">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.75rem', alignItems: 'start' }}>
                      {/* Left Column: Top Headers, Video Link, Live Preview & Button */}
                      <div style={{ background: '#070c18', padding: '1.5rem', borderRadius: '10px', border: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        <div style={{ borderBottom: '1px solid #1e293b', paddingBottom: '0.65rem' }}>
                          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#c4a882' }}>🎬 Video & Top Banner</span>
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Top Badge Tag <span className="req">*</span></label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. SEE IT IN ACTION"
                            value={item.top_tag || ''}
                            onChange={(e) => updateYoutubeField('top_tag', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Main Top Heading <span className="req">*</span></label>
                          <textarea 
                            className="textarea-custom"
                            rows={2}
                            placeholder="e.g. India's Premier Wardrobe & Modular Kitchen Manufacturer"
                            value={item.top_heading || ''}
                            onChange={(e) => updateYoutubeField('top_heading', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">YouTube Video Link / URL <span className="req">*</span></label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. https://youtu.be/DobkQyCEkVo?si=..."
                            value={item.video_url || item.video || ''}
                            onChange={(e) => {
                              updateYoutubeField('video_url', e.target.value);
                              updateYoutubeField('video', e.target.value);
                            }}
                          />
                          <span className="helper-text">Accepts youtube.com/watch, youtu.be, or video ID.</span>
                        </div>

                        {/* Live YouTube Preview */}
                        <div>
                          <label className="label-custom" style={{ marginBottom: '0.4rem' }}>Live Player Preview</label>
                          <div 
                            style={{ 
                              position: 'relative',
                              width: '100%', 
                              aspectRatio: '16/9', 
                              background: '#000000', 
                              borderRadius: '10px', 
                              overflow: 'hidden',
                              border: '1px solid #1e293b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {(item.video_url || item.video) ? (
                              <iframe
                                src={getEmbedUrl(item.video_url || item.video)}
                                title="YouTube video player"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                style={{ width: '100%', height: '100%', border: 'none' }}
                              />
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
                                <FaYoutube size={40} style={{ color: '#ef4444' }} />
                                <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Paste YouTube URL to preview video</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Button controls */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.25rem' }}>
                          <div className="form-group-custom" style={{ margin: 0 }}>
                            <label className="label-custom">Button Text</label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="e.g. EXPLORE OUR PROCESS"
                              value={item.button_text || ''}
                              onChange={(e) => updateYoutubeField('button_text', e.target.value)}
                            />
                          </div>

                          <div className="form-group-custom" style={{ margin: 0 }}>
                            <label className="label-custom">Button Target URL</label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="e.g. #explore"
                              value={item.button_url || ''}
                              onChange={(e) => updateYoutubeField('button_url', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right Column: Craftsmanship Story & Feature Checklist */}
                      <div style={{ background: '#070c18', padding: '1.5rem', borderRadius: '10px', border: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        <div style={{ borderBottom: '1px solid #1e293b', paddingBottom: '0.65rem' }}>
                          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#c4a882' }}>✨ Craftsmanship Content & Checklist</span>
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Side Badge Tag <span className="req">*</span></label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. CRAFTSMANSHIP IN MOTION"
                            value={item.side_tag || ''}
                            onChange={(e) => updateYoutubeField('side_tag', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Side Heading <span className="req">*</span></label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. Watch How We Build Spaces That Feel Like Home"
                            value={item.side_heading || ''}
                            onChange={(e) => updateYoutubeField('side_heading', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Description Paragraph <span className="req">*</span></label>
                          <textarea 
                            className="textarea-custom"
                            rows={4}
                            placeholder="e.g. Take a behind-the-scenes look at our state-of-the-art manufacturing unit..."
                            value={item.description || item.desc || ''}
                            onChange={(e) => {
                              updateYoutubeField('description', e.target.value);
                              updateYoutubeField('desc', e.target.value);
                            }}
                          />
                        </div>

                        {/* Feature Checklist */}
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
                            <label className="label-custom" style={{ margin: 0 }}>Feature Checklist Points</label>
                            <button 
                              type="button" 
                              className="btn-add-dashed"
                              style={{ width: 'auto', padding: '0.25rem 0.75rem', fontSize: '0.75rem', margin: 0 }}
                              onClick={addFeatureItem}
                            >
                              <FaPlus size={10} /> Add Point
                            </button>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                            {currentFeatures.map((feat, fIdx) => (
                              <div key={fIdx} style={{ display: 'flex', gap: '0.65rem', alignItems: 'center' }}>
                                <span style={{ color: '#22c55e', fontSize: '1rem', fontWeight: 'bold' }}>✓</span>
                                <input 
                                  type="text" 
                                  className="input-custom"
                                  placeholder={`Point #${fIdx + 1}`}
                                  value={feat}
                                  onChange={(e) => updateFeatureItem(fIdx, e.target.value)}
                                  style={{ flex: 1 }}
                                />
                                {currentFeatures.length > 1 && (
                                  <button 
                                    type="button" 
                                    className="btn-remove-icon"
                                    onClick={() => removeFeatureItem(fIdx)}
                                    title="Remove Point"
                                    style={{ flexShrink: 0 }}
                                  >
                                    <FaTrash size={13} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* RENDER MODE D: Left Content & Right Image / Brand Celebration (section_id = 4) */}
              {activeSecId === 4 && (() => {
                const item = (Array.isArray(currentContent.content) && currentContent.content.length > 0)
                  ? currentContent.content[0]
                  : (currentContent.content || {});

                const updateBrandField = (field, val) => {
                  const fresh = { ...widgetContents };
                  if (!fresh[4]) fresh[4] = { section_id: 4, content: [{}] };
                  if (!Array.isArray(fresh[4].content)) fresh[4].content = [{}];
                  if (fresh[4].content.length === 0) fresh[4].content = [{}];
                  fresh[4].content[0][field] = val;
                  setWidgetContents(fresh);
                };

                return (
                  <div className="widget-card-block animate-fade-up">
                    {/* Top Row: Left Image & Right Header Info */}
                    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                      {/* Left Graphic / Celebration Image */}
                      <div>
                        <label className="label-custom">Celebration / Left Graphic Image <span className="req">*</span></label>
                        <div 
                          className="widget-uploader-wrapper"
                          style={{ height: '220px', background: '#0b101d' }}
                          onClick={() => fileInputRefs.current['brand_left_img']?.click()}
                        >
                          {item.image ? (
                            <>
                              <img src={getImageUrl(item.image)} alt={item.alt || '15 Years'} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '6px' }} />
                              <div className="image-delete-overlay" onClick={(e) => { e.stopPropagation(); updateBrandField('image', ''); }}>
                                <FaTrash size={20} />
                              </div>
                            </>
                          ) : (
                            <div className="uploader-placeholder">
                              <FaCloudUploadAlt size={36} />
                              <span>Click to upload 15+ Years Graphic</span>
                            </div>
                          )}
                        </div>
                        <input 
                          type="file" 
                          accept="image/*"
                          style={{ display: 'none' }}
                          ref={el => fileInputRefs.current['brand_left_img'] = el}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('image', file);
                              try {
                                const res = await axiosInstance.post('/products/upload', formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                if (res.data && res.data.filename) {
                                  updateBrandField('image', res.data.filename);
                                  toast.success('Celebration image uploaded successfully!');
                                }
                              } catch (err) {
                                console.error('Image upload failed:', err);
                                toast.error('Failed to upload image');
                              }
                            }
                          }}
                        />
                        <span className="helper-text">Recommended: 600 × 600 px square / banner graphic.</span>

                        <div className="form-group-custom" style={{ marginTop: '1rem', marginBottom: 0 }}>
                          <label className="label-custom">Image Alt Text</label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. 15 Years of Sliding"
                            value={item.alt || ''}
                            onChange={(e) => updateBrandField('alt', e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Right Header & Badge */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Badge Tag <span className="req">*</span></label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. ABOUT ARISTO INDIA"
                            value={item.tag || ''}
                            onChange={(e) => updateBrandField('tag', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Main Section Heading <span className="req">*</span></label>
                          <textarea 
                            className="textarea-custom"
                            rows={3}
                            placeholder="e.g. India's Leading Wardrobe & Modular Kitchen Manufacturer Brand"
                            value={item.heading || ''}
                            onChange={(e) => updateBrandField('heading', e.target.value)}
                          />
                        </div>

                        {/* Button Configuration */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div className="form-group-custom" style={{ margin: 0 }}>
                            <label className="label-custom">Button Text <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="e.g. Explore More"
                              value={item.btn_text || item.buttonText1 || ''}
                              onChange={(e) => updateBrandField('btn_text', e.target.value)}
                            />
                          </div>

                          <div className="form-group-custom" style={{ margin: 0 }}>
                            <label className="label-custom">Button Target URL <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="e.g. /about-us"
                              value={item.btn_url || item.buttonUrl1 || ''}
                              onChange={(e) => updateBrandField('btn_url', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Descriptions Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
                      <div className="form-group-custom" style={{ margin: 0 }}>
                        <label className="label-custom">Description Paragraph 1 <span className="req">*</span></label>
                        <textarea 
                          className="textarea-custom"
                          rows={6}
                          placeholder="Enter first paragraph (With a legacy spanning over two decades...)"
                          value={item.desc1 || item.description || ''}
                          onChange={(e) => {
                            updateBrandField('desc1', e.target.value);
                            updateBrandField('description', e.target.value);
                          }}
                        />
                      </div>

                      <div className="form-group-custom" style={{ margin: 0 }}>
                        <label className="label-custom">Description Paragraph 2 <span className="req">*</span></label>
                        <textarea 
                          className="textarea-custom"
                          rows={6}
                          placeholder="Enter second paragraph (Aristo India is a strategic joint venture...)"
                          value={item.desc2 || ''}
                          onChange={(e) => updateBrandField('desc2', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* 4 Stat Highlights Counters */}
                    <div>
                      <label className="label-custom" style={{ marginBottom: '0.75rem', display: 'block' }}>Key Highlights & Stat Counters</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                        {/* Stat 1 */}
                        <div style={{ background: '#070c18', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.75rem', color: '#c4a882', fontWeight: 'bold' }}>Counter 1</span>
                          <input 
                            type="text" 
                            className="input-custom" 
                            style={{ margin: '0.35rem 0' }}
                            placeholder="Value (e.g. 1000+)"
                            value={item.stat1_val || ''}
                            onChange={(e) => updateBrandField('stat1_val', e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Label (e.g. Premium Designs)"
                            value={item.stat1_lbl || ''}
                            onChange={(e) => updateBrandField('stat1_lbl', e.target.value)}
                          />
                        </div>

                        {/* Stat 2 */}
                        <div style={{ background: '#070c18', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.75rem', color: '#c4a882', fontWeight: 'bold' }}>Counter 2</span>
                          <input 
                            type="text" 
                            className="input-custom" 
                            style={{ margin: '0.35rem 0' }}
                            placeholder="Value (e.g. 200000+)"
                            value={item.stat2_val || ''}
                            onChange={(e) => updateBrandField('stat2_val', e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Label (e.g. Happy Customer)"
                            value={item.stat2_lbl || ''}
                            onChange={(e) => updateBrandField('stat2_lbl', e.target.value)}
                          />
                        </div>

                        {/* Stat 3 */}
                        <div style={{ background: '#070c18', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.75rem', color: '#c4a882', fontWeight: 'bold' }}>Counter 3</span>
                          <input 
                            type="text" 
                            className="input-custom" 
                            style={{ margin: '0.35rem 0' }}
                            placeholder="Value (e.g. 16+)"
                            value={item.stat3_val || ''}
                            onChange={(e) => updateBrandField('stat3_val', e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Label (e.g. Display Center In India)"
                            value={item.stat3_lbl || ''}
                            onChange={(e) => updateBrandField('stat3_lbl', e.target.value)}
                          />
                        </div>

                        {/* Stat 4 */}
                        <div style={{ background: '#070c18', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <span style={{ fontSize: '0.75rem', color: '#c4a882', fontWeight: 'bold' }}>Counter 4</span>
                          <input 
                            type="text" 
                            className="input-custom" 
                            style={{ margin: '0.35rem 0' }}
                            placeholder="Value (e.g. Global)"
                            value={item.stat4_val || ''}
                            onChange={(e) => updateBrandField('stat4_val', e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="input-custom" 
                            placeholder="Label (e.g. Presence)"
                            value={item.stat4_lbl || ''}
                            onChange={(e) => updateBrandField('stat4_lbl', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* RENDER MODE: Why Choose Aristo (section_id = 10) */}
              {activeSecId === 10 && (() => {
                const item = (Array.isArray(currentContent.content) && currentContent.content.length > 0)
                  ? currentContent.content[0]
                  : (currentContent.content || {});

                const updateWhyField = (field, val) => {
                  const fresh = { ...widgetContents };
                  if (!fresh[10]) fresh[10] = { section_id: 10, content: [{}] };
                  if (!Array.isArray(fresh[10].content)) fresh[10].content = [{}];
                  if (fresh[10].content.length === 0) fresh[10].content = [{}];
                  fresh[10].content[0][field] = val;
                  setWidgetContents(fresh);
                };

                const currentFeatures = Array.isArray(item.features) ? item.features : [
                  { title: 'German Precision', desc: 'Sourced from the best in class.', icon: 'gear' },
                  { title: 'Custom Fabrication', desc: 'Crafted unique to every space.', icon: 'tool' },
                  { title: 'Flawless Design', desc: '100% design to execution.', icon: 'ruler' },
                  { title: 'Sustainable Choices', desc: 'Eco-friendly materials only.', icon: 'leaf' },
                  { title: '15+ Years Excellence', desc: 'Over a decade in luxury.', icon: 'award' },
                  { title: 'Global Presence', desc: 'Accepted worldwide.', icon: 'globe' }
                ];

                const updateFeatureItem = (fIdx, key, val) => {
                  const updatedFeatures = [...currentFeatures];
                  updatedFeatures[fIdx] = { ...updatedFeatures[fIdx], [key]: val };
                  updateWhyField('features', updatedFeatures);
                };

                return (
                  <div className="widget-card-block animate-fade-up">
                    {/* Top Row: Left Header & Right Showcase Image */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem', marginBottom: '1.5rem' }}>
                      {/* Left: Badge Tag, Heading, Description */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Badge Tag <span className="req">*</span></label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. WHY CHOOSE ARISTO"
                            value={item.tag || ''}
                            onChange={(e) => updateWhyField('tag', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Main Section Heading <span className="req">*</span></label>
                          <textarea 
                            className="textarea-custom"
                            rows={2}
                            placeholder="e.g. Excellence in Every Detail, Trust in Every Experience."
                            value={item.heading || ''}
                            onChange={(e) => updateWhyField('heading', e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom" style={{ margin: 0 }}>
                          <label className="label-custom">Section Description <span className="req">*</span></label>
                          <textarea 
                            className="textarea-custom"
                            rows={3}
                            placeholder="e.g. At Aristo, we believe that true luxury lies in the details..."
                            value={item.description || item.desc || ''}
                            onChange={(e) => {
                              updateWhyField('description', e.target.value);
                              updateWhyField('desc', e.target.value);
                            }}
                          />
                        </div>
                      </div>

                      {/* Right: Showcase Background Image */}
                      <div>
                        <label className="label-custom">Showcase Background Image <span className="req">*</span></label>
                        <div 
                          className="widget-uploader-wrapper"
                          style={{ height: '200px', background: '#0b101d' }}
                          onClick={() => fileInputRefs.current['why_choose_img']?.click()}
                        >
                          {item.image ? (
                            <>
                              <img src={getImageUrl(item.image)} alt={item.alt || 'Showcase'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <div className="image-delete-overlay" onClick={(e) => { e.stopPropagation(); updateWhyField('image', ''); }}>
                                <FaTrash size={20} />
                              </div>
                            </>
                          ) : (
                            <div className="uploader-placeholder">
                              <FaCloudUploadAlt size={36} />
                              <span>Click to upload Kitchen Showcase Image</span>
                            </div>
                          )}
                        </div>
                        <input 
                          type="file" 
                          accept="image/*"
                          style={{ display: 'none' }}
                          ref={el => fileInputRefs.current['why_choose_img'] = el}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('image', file);
                              try {
                                const res = await axiosInstance.post('/products/upload', formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                if (res.data && res.data.filename) {
                                  updateWhyField('image', res.data.filename);
                                  toast.success('Showcase image uploaded successfully!');
                                }
                              } catch (err) {
                                console.error('Image upload failed:', err);
                                toast.error('Failed to upload image');
                              }
                            }
                          }}
                        />
                        <span className="helper-text">Recommended: 1000 × 700 px high-res showcase photo.</span>

                        <div className="form-group-custom" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                          <label className="label-custom">Image Alt Text</label>
                          <input 
                            type="text" 
                            className="input-custom"
                            placeholder="e.g. Why Choose Aristo"
                            value={item.alt || ''}
                            onChange={(e) => updateWhyField('alt', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Features (6 USP Grid) */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
                        <label className="label-custom" style={{ margin: 0 }}>6 Distinct Features / USPs</label>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
                        {currentFeatures.map((f, fIdx) => (
                          <div key={fIdx} style={{ background: '#070c18', padding: '1rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                              <span style={{ fontSize: '0.8rem', color: '#c4a882', fontWeight: 'bold' }}>Feature #{fIdx + 1}</span>
                              <select 
                                className="input-custom" 
                                style={{ width: 'auto', padding: '0.2rem 0.5rem', fontSize: '0.75rem', height: '28px' }}
                                value={f.icon || 'gear'}
                                onChange={(e) => updateFeatureItem(fIdx, 'icon', e.target.value)}
                              >
                                <option value="gear">⚙️ Gear (Precision)</option>
                                <option value="tool">🔨 Tool (Fabrication)</option>
                                <option value="ruler">📐 Ruler (Design)</option>
                                <option value="leaf">🌿 Leaf (Sustainable)</option>
                                <option value="award">🏆 Award (Excellence)</option>
                                <option value="globe">🌐 Globe (Presence)</option>
                              </select>
                            </div>

                            <input 
                              type="text" 
                              className="input-custom" 
                              style={{ marginBottom: '0.5rem' }}
                              placeholder="Title (e.g. German Precision)"
                              value={f.title || ''}
                              onChange={(e) => updateFeatureItem(fIdx, 'title', e.target.value)}
                            />

                            <input 
                              type="text" 
                              className="input-custom" 
                              placeholder="Description (e.g. Sourced from the best in class.)"
                              value={f.desc || ''}
                              onChange={(e) => updateFeatureItem(fIdx, 'desc', e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* RENDER MODE E: Banners (Home Slider=1, Banner for Our Partner=2, Wide Banner=3) */}
              {[1, 2, 3].includes(activeSecId) && (
                <>
                  {Array.isArray(currentContent.content) && currentContent.content.map((slide, sIdx) => (
                    <div key={slide.id || sIdx} className="widget-card-block" style={{ marginBottom: '1.5rem' }}>
                      <div className="slide-header-bar">
                        <h3 className="slide-number-title">Slide {sIdx + 1}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <label className="switch-toggle" style={{ transform: 'scale(0.85)', marginBottom: 0 }}>
                            <input 
                              type="checkbox" 
                              checked={slide.status === 1 || slide.status === '1'}
                              onChange={(e) => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].status = e.target.checked ? 1 : 0;
                                setWidgetContents(fresh);
                              }}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <button 
                            type="button" 
                            className="btn-trash-icon"
                            onClick={() => handleRemoveSlideItem(activeSecId, sIdx)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>

                      {/* Banner Split columns: Image + Alt on left, Title + Desc on right */}
                      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                        <div>
                          <label className="label-custom">Slider Image <span className="req">*</span></label>
                          <div 
                            className="widget-uploader-wrapper"
                            style={{ background: slide.image ? '#ffffff' : 'transparent' }}
                            onClick={() => fileInputRefs.current[`slide_img_${sIdx}`]?.click()}
                          >
                            {slide.image ? (
                              <>
                                <img src={getImageUrl(slide.image)} alt={slide.alt} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }} />
                                <div className="image-delete-overlay" onClick={(e) => { e.stopPropagation(); const fresh = { ...widgetContents }; fresh[activeSecId].content[sIdx].image = ''; setWidgetContents(fresh); }}>
                                  <FaTrash size={20} />
                                </div>
                              </>
                            ) : (
                              <div className="uploader-placeholder">
                                <FaCloudUploadAlt size={36} />
                                <span>Upload Slider Image</span>
                              </div>
                            )}
                          </div>
                          <input 
                            type="file" 
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={el => fileInputRefs.current[`slide_img_${sIdx}`] = el}
                            onChange={(e) => handleSlideImageChange(e, activeSecId, sIdx)}
                          />
                          <span className="helper-text" style={{ display: 'block', marginBottom: '1rem' }}>Dimensions 800 × 500 px.</span>

                          <div className="form-group-custom">
                            <label className="label-custom">Alt Text <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Alt Text"
                              value={slide.alt || ''}
                              onChange={(e) => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].alt = e.target.value;
                                setWidgetContents(fresh);
                              }}
                            />
                          </div>
                        </div>

                        <div>
                          <div className="form-group-custom">
                            <label className="label-custom">Slider Image Title <span className="opt">(Optional)</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Slider Image Title"
                              value={slide.heading || ''}
                              onChange={(e) => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].heading = e.target.value;
                                setWidgetContents(fresh);
                              }}
                            />
                          </div>

                          <div className="form-group-custom">
                            <label className="label-custom">Slider Image Description <span className="opt">(Optional)</span></label>
                            <textarea 
                              className="textarea-custom"
                              rows={5}
                              placeholder="Slider Image Description"
                              value={slide.description || ''}
                              onChange={(e) => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].description = e.target.value;
                                setWidgetContents(fresh);
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Action Button config */}
                      <div className="form-group-custom" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '1.25rem' }}>
                        <label className="label-custom">Do you want to assign an action button?</label>
                        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                            <input 
                              type="radio" 
                              name={`assign_btn_banner_${sIdx}`}
                              checked={slide.assign_btn === '1' || slide.assign_btn === 1}
                              onChange={() => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].assign_btn = '1';
                                setWidgetContents(fresh);
                              }}
                            /> Yes
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                            <input 
                              type="radio" 
                              name={`assign_btn_banner_${sIdx}`}
                              checked={slide.assign_btn === '0' || slide.assign_btn === 0}
                              onChange={() => {
                                const fresh = { ...widgetContents };
                                fresh[activeSecId].content[sIdx].assign_btn = '0';
                                setWidgetContents(fresh);
                              }}
                            /> No
                          </label>
                        </div>
                      </div>

                      {(slide.assign_btn === '1' || slide.assign_btn === 1) && (
                        <div className="action-button-form-block animate-fade-up">
                          <h4 className="button-group-heading">Action Button Settings</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div className="form-group-custom">
                              <label className="label-custom">Button Link Type</label>
                              <select 
                                className="input-custom"
                                value={slide.urlType1 || ''}
                                onChange={(e) => {
                                  const fresh = { ...widgetContents };
                                  fresh[activeSecId].content[sIdx].urlType1 = e.target.value;
                                  setWidgetContents(fresh);
                                }}
                              >
                                <option value="">Choose Link Type</option>
                                <option value="0">Category Redirect</option>
                                <option value="2">Custom URL</option>
                              </select>
                            </div>

                            <div className="form-group-custom">
                              <label className="label-custom">Button Text</label>
                              <input 
                                type="text" 
                                className="input-custom"
                                placeholder="e.g. Explore More"
                                value={slide.buttonText1 || ''}
                                onChange={(e) => {
                                  const fresh = { ...widgetContents };
                                  fresh[activeSecId].content[sIdx].buttonText1 = e.target.value;
                                  setWidgetContents(fresh);
                                }}
                              />
                            </div>
                          </div>

                          <div className="form-group-custom">
                            <label className="label-custom">Target URL / Category</label>
                            {slide.urlType1 === '0' ? (
                              <select 
                                className="input-custom"
                                value={slide.buttonUrl1 || ''}
                                onChange={(e) => {
                                  const fresh = { ...widgetContents };
                                  fresh[activeSecId].content[sIdx].buttonUrl1 = e.target.value;
                                  setWidgetContents(fresh);
                                }}
                              >
                                {renderPageRedirectOptions()}
                              </select>
                            ) : (
                              <input 
                                type="text" 
                                className="input-custom"
                                placeholder="e.g. /custom-route-page"
                                value={slide.buttonUrl1 || ''}
                                onChange={(e) => {
                                  const fresh = { ...widgetContents };
                                  fresh[activeSecId].content[sIdx].buttonUrl1 = e.target.value;
                                  setWidgetContents(fresh);
                                }}
                              />
                            )}
                          </div>
                        </div>
                      )}

                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                    <button 
                      type="button" 
                      className="btn-add-dashed"
                      onClick={() => handleAddSlideItem(activeSecId)}
                    >
                      <FaPlus /> Add Banner Slide
                    </button>
                  </div>
                </>
              )}

              {/* Footer Action Bar */}
              <div className="edit-widget-footer">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => navigate('/page-list')}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn-save"
                  onClick={handleSaveAll}
                >
                  Save
                </button>
              </div>

            </div>
          )}
        </div>
      </div>

      {/* Testimonial Modals */}
      {showAddTestimonialModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={modalHeaderStyle}>
              <h3 style={modalTitleStyle}>Add New Testimonial</h3>
              <button 
                type="button" 
                onClick={() => setShowAddTestimonialModal(false)}
                style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleCreateTestimonial}>
              <div className="form-group-custom" style={{ marginBottom: '1.25rem' }}>
                <label className="label-custom">Customer Name <span className="req">*</span></label>
                <input 
                  type="text" 
                  className="input-custom" 
                  value={newTestimonialName}
                  onChange={(e) => setNewTestimonialName(e.target.value)}
                  placeholder="e.g. John Doe"
                  required
                />
              </div>

              <div className="form-group-custom" style={{ marginBottom: '1.25rem' }}>
                <label className="label-custom">Location <span className="opt">(Optional)</span></label>
                <input 
                  type="text" 
                  className="input-custom" 
                  value={newTestimonialLocation}
                  onChange={(e) => setNewTestimonialLocation(e.target.value)}
                  placeholder="e.g. Mumbai, India"
                />
              </div>

              <div className="form-group-custom" style={{ marginBottom: '1.25rem' }}>
                <label className="label-custom">Rating</label>
                <select 
                  className="input-custom" 
                  value={newTestimonialRating} 
                  onChange={(e) => setNewTestimonialRating(e.target.value)}
                  style={{ background: '#070a13', border: '1px solid #1e293b', color: '#ffffff' }}
                >
                  <option value="5">⭐⭐⭐⭐⭐ (5 Star)</option>
                  <option value="4">⭐⭐⭐⭐ (4 Star)</option>
                  <option value="3">⭐⭐⭐ (3 Star)</option>
                  <option value="2">⭐⭐ (2 Star)</option>
                  <option value="1">⭐ (1 Star)</option>
                </select>
              </div>

              <div className="form-group-custom" style={{ marginBottom: '1.5rem' }}>
                <label className="label-custom">Review / Feedback <span className="req">*</span></label>
                <textarea 
                  className="input-custom" 
                  style={{ minHeight: '120px', resize: 'vertical' }}
                  value={newTestimonialReview}
                  onChange={(e) => setNewTestimonialReview(e.target.value)}
                  placeholder="Write customer review text here..."
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={() => setShowAddTestimonialModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-save"
                >
                  Add Testimonial
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showManageTestimonialsModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '750px' }}>
            <div style={modalHeaderStyle}>
              <h3 style={modalTitleStyle}>Manage Testimonials</h3>
              <button 
                type="button" 
                onClick={() => setShowManageTestimonialsModal(false)}
                style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                &times;
              </button>
            </div>

            <div style={{ maxHeight: '50vh', overflowY: 'auto', marginBottom: '1.5rem' }}>
              {allTestimonials.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>No testimonials found in database.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', color: '#ffffff' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1e293b', textAlign: 'left' }}>
                      <th style={{ padding: '0.75rem 0.5rem', color: '#94a3b8' }}>Name</th>
                      <th style={{ padding: '0.75rem 0.5rem', color: '#94a3b8' }}>Location</th>
                      <th style={{ padding: '0.75rem 0.5rem', color: '#94a3b8' }}>Rating</th>
                      <th style={{ padding: '0.75rem 0.5rem', color: '#94a3b8', width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTestimonials.map(t => (
                      <tr key={t.id} style={{ borderBottom: '1px solid #0f172a' }}>
                        <td style={{ padding: '0.75rem 0.5rem', fontWeight: 'bold' }}>{t.name}</td>
                        <td style={{ padding: '0.75rem 0.5rem', color: '#94a3b8' }}>{t.location || '-'}</td>
                        <td style={{ padding: '0.75rem 0.5rem' }}>{'⭐'.repeat(t.rating || 5)}</td>
                        <td style={{ padding: '0.75rem 0.5rem' }}>
                          <button 
                            type="button" 
                            onClick={() => handleDeleteTestimonial(t)}
                            style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                          >
                            <FaTrash size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="btn-save" 
                onClick={() => setShowManageTestimonialsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTestimonialTarget}
        type="delete"
        title="Delete Testimonial"
        itemName={deleteTestimonialTarget?.name}
        itemType="testimonial"
        loading={isDeletingTestimonial}
        onConfirm={handleConfirmDeleteTestimonial}
        onCancel={() => setDeleteTestimonialTarget(null)}
      />

    </div>
  );
};

export default WidgetManager;
